// Config and hooks for the `markdown` sanitize preset in `@github-ui/sanitize`.
// Selected via `sanitize(html, {preset: 'markdown'})`; use it to sanitize untrusted
// GitHub-Flavored Markdown block content.
//
// The config follows @github-ui/md2html's closed standard-HTML baseline. Trusted
// goomba-enriched Markdown uses VerifiedHTML instead, preserving its server-injected
// gadgets without re-sanitizing. Output is minted through the dedicated
// `github-sanitize-markdown` Trusted Types policy (see `trusted-types.ts`); a strict
// preset never reuses the lax `github-sanitize-lax` policy.

import type {Config, Purifier} from '../types'
import {sanitizeNewWindowLinks, SHARED_CONFIG} from './shared-hooks'

// Markdown config — closed standard-HTML allowlist layered on the shared default-deny base.
export const MARKDOWN_SANITIZE_CONFIG: Readonly<Config> = {
  ...SHARED_CONFIG,
  ALLOWED_TAGS: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'h7',
    'h8',
    'br',
    'b',
    'i',
    'strong',
    'em',
    'a',
    'pre',
    'code',
    'img',
    'tt',
    'div',
    'span',
    'ins',
    'del',
    'sup',
    'sub',
    'p',
    'ol',
    'ul',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'blockquote',
    'dl',
    'dt',
    'dd',
    'kbd',
    'q',
    'samp',
    'var',
    'hr',
    'ruby',
    'rt',
    'rp',
    'li',
    'tr',
    'td',
    'th',
    's',
    'strike',
    'summary',
    'details',
    'picture',
    'source',
  ],
  FORBID_ATTR: ['class', 'id', 'style', 'hidden'],
}

/**
 * Attach the markdown preset's relevant security hooks to a purifier instance: link
 * hardening. External image URLs are preserved and sanitized by DOMPurify.
 * Its closed config already rejects every
 * class and data-* attribute, so it does not attach the lax path's class and
 * URI-safe data-attribute hooks. The link hook runs on both client
 * and server (linkedom exposes `target`). Called from each entry's local
 * `createPurifier`.
 */
export function addMarkdownSanitizeHooks(instance: Purifier): void {
  instance.addHook('afterSanitizeAttributes', sanitizeNewWindowLinks)
}
